import { useState } from 'react'
import { useNavigate, Link, useSearchParams } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { Building2, User, Loader2 } from 'lucide-react'
import './auth/Auth.css'

export default function Login() {
    const [searchParams] = useSearchParams()
    const initialRole = (searchParams.get('role') as 'landlord' | 'tenant') || 'landlord'

    const [selectedRole, setSelectedRole] = useState<'landlord' | 'tenant'>(initialRole)
    const [email, setEmail] = useState('')
    const [password, setPassword] = useState('')
    const [error, setError] = useState<string | null>(null)
    const [loading, setLoading] = useState(false)
    const navigate = useNavigate()

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        setError(null)
        setLoading(true)

        try {
            const { data, error: loginError } = await supabase.auth.signInWithPassword({
                email,
                password,
            })

            if (loginError) {
                setError(loginError.message)
                return
            }

            if (!data.user) {
                setError('Login failed. Please try again.')
                return
            }

            // ── Determine true role ───────────────────────────────────────────
            // user_metadata.role is written at sign-up time and is the most
            // reliable source. profile.role is the DB record (may be stale).
            const metaRole = data.user.user_metadata?.role as string | undefined

            const { data: profile } = await supabase
                .from('profiles')
                .select('role')
                .eq('id', data.user.id)
                .maybeSingle()

            const dbRole = profile?.role as string | undefined

            // Healing: if the metadata says 'tenant' but DB says 'landlord',
            // the profile was created incorrectly — fix it now.
            let effectiveRole: 'landlord' | 'tenant'

            if (metaRole === 'tenant' || dbRole === 'tenant') {
                // Either source confirms tenant — trust it.
                effectiveRole = 'tenant'

                // Heal the DB if it's out of sync.
                if (dbRole !== 'tenant') {
                    await supabase
                        .from('profiles')
                        .update({ role: 'tenant' })
                        .eq('id', data.user.id)
                }
            } else if (metaRole === 'landlord' || dbRole === 'landlord') {
                effectiveRole = 'landlord'
            } else {
                // No profile data at all — use the toggle the user selected.
                effectiveRole = selectedRole

                // Create the profile so this doesn't happen again.
                await supabase.from('profiles').upsert({
                    id: data.user.id,
                    email: data.user.email,
                    role: selectedRole,
                })
            }

            navigate(
                effectiveRole === 'tenant'
                    ? '/app/tenant/dashboard'
                    : '/app/landlord/dashboard',
                { replace: true }
            )
        } catch (err) {
            console.error('[LOGIN] unexpected error:', err)
            setError('An unexpected error occurred. Please try again.')
        } finally {
            setLoading(false)
        }
    }

    const toggleStyle = (active: boolean): React.CSSProperties => ({
        flex: 1,
        padding: '0.5rem 0.75rem',
        border: 'none',
        background: active ? '#fff' : 'transparent',
        borderRadius: '6px',
        cursor: 'pointer',
        fontWeight: 600,
        fontSize: '0.875rem',
        boxShadow: active ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '6px',
        color: active ? 'var(--color-text)' : 'var(--color-text-tertiary)',
        transition: 'all 0.2s',
    })

    return (
        <div className="auth-layout">
            <div className="auth-grid" />
            <div className="auth-orb auth-orb--1" />
            <div className="auth-orb auth-orb--2" />
            <div className="auth-orb auth-orb--3" />
            <div className="auth-orb auth-orb--4" />
            <div className="auth-orb auth-orb--5" />
            <div className="auth-container">
                <div className="auth-logo" style={{ marginBottom: '1.5rem' }}>
                    <img
                        src="/rentnova-logo.png"
                        alt="RentNova — Property Management Tools"
                        style={{ width: 200, height: 'auto', objectFit: 'contain', display: 'block' }}
                    />
                </div>

                <div className="auth-card">
                    <h1 className="auth-title">Welcome back</h1>
                    <p className="auth-subtitle">
                        {selectedRole === 'tenant'
                            ? 'Log in to your tenant portal.'
                            : 'Log in to manage your properties.'}
                    </p>

                    {/* Role Toggle */}
                    <div style={{
                        display: 'flex',
                        gap: '0.25rem',
                        marginBottom: '1.75rem',
                        padding: '0.25rem',
                        background: 'var(--color-bg-muted)',
                        borderRadius: '8px',
                    }}>
                        <button
                            type="button"
                            onClick={() => setSelectedRole('landlord')}
                            style={toggleStyle(selectedRole === 'landlord')}
                        >
                            <Building2 size={15} /> I'm a Landlord
                        </button>
                        <button
                            type="button"
                            onClick={() => setSelectedRole('tenant')}
                            style={toggleStyle(selectedRole === 'tenant')}
                        >
                            <User size={15} /> I'm a Tenant
                        </button>
                    </div>

                    <form className="auth-form" onSubmit={handleSubmit}>
                        {error && (
                            <div style={{
                                color: '#b91c1c',
                                backgroundColor: '#fee2e2',
                                padding: '0.75rem 1rem',
                                borderRadius: 'var(--radius-md)',
                                fontSize: '0.875rem',
                                marginBottom: '0.25rem',
                                lineHeight: 1.5,
                            }}>
                                {error}
                            </div>
                        )}

                        <div className="auth-field">
                            <label>Email</label>
                            <input
                                type="email"
                                placeholder="you@example.com"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                autoComplete="email"
                            />
                        </div>
                        <div className="auth-field">
                            <label>Password</label>
                            <input
                                type="password"
                                placeholder="••••••••"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                                autoComplete="current-password"
                            />
                        </div>

                        <button
                            type="submit"
                            className="btn btn-primary"
                            style={{
                                width: '100%',
                                marginTop: '0.5rem',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '8px',
                            }}
                            disabled={loading}
                        >
                            {loading ? (
                                <><Loader2 size={16} className="animate-spin" /> Logging in…</>
                            ) : selectedRole === 'tenant' ? (
                                'Log in as Tenant'
                            ) : (
                                'Log in as Landlord'
                            )}
                        </button>
                    </form>
                </div>

                <p className="auth-footer">
                    Don't have an account?{' '}
                    <Link to={`/register?role=${selectedRole}`}>Register</Link>
                </p>
            </div>
        </div>
    )
}
